import { Mic, Square } from 'lucide-react';
import React, { useEffect, useState, useRef } from 'react';

import { IconButton } from '../icon-button';

import { DEFAULT_MAX_DURATION } from './constants/audio';
import { useMediaRecorder } from './hooks/useMediaRecorder';
import styles from './styles.module.css';
import { VoiceInputProps } from './types';

export const VoiceInput: React.FC<VoiceInputProps> = props => {
  const {
    maxDuration = DEFAULT_MAX_DURATION,
    onError,
    onRecordingComplete,
    onRecordingStart,
    onRecordingStop,
    onRecordingStateChange,
  } = props;

  const [timeLeft, setTimeLeft] = useState(maxDuration);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const { isRecording, startRecording, stopRecording, error } =
    useMediaRecorder({
      onRecordingComplete,
      onRecordingStart,
      onRecordingStop,
      onError,
    });

  useEffect(() => {
    onRecordingStateChange(isRecording);
  }, [isRecording, onRecordingStateChange]);

  // Handle countdown timer
  useEffect(() => {
    if (!isRecording) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setTimeLeft(maxDuration);
      return;
    }

    setTimeLeft(maxDuration);
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          stopRecording();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isRecording, maxDuration, stopRecording]);

  // Handle max duration timeout
  useEffect(() => {
    if (!isRecording) return;

    const timeout = setTimeout(() => {
      stopRecording();
    }, maxDuration * 1000);

    return () => clearTimeout(timeout);
  }, [isRecording, maxDuration, stopRecording]);

  const handleButtonClick = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  return (
    <div className={`${styles.container} ${props.className || ''}`}>
      <div className={styles.controls}>
        <IconButton
          className={`${styles.recordButton} ${isRecording ? styles.recording : ''}`}
          onClick={handleButtonClick}
          disabled={props.disabled || !!error}
        >
          {isRecording ? <Square size={16} /> : <Mic size={20} />}
        </IconButton>
        {isRecording && <div className={styles.timer}>{timeLeft}s</div>}
      </div>
      {error && <div className={styles.error}>{error.message}</div>}
    </div>
  );
};
